/**
 * Password reset view (E01-F03, web side of T02/T03/T04) — the migration-flow
 * screen a user is sent to when they carry `password_reset_required` (the API
 * answers a protected request with a 403 whose `error.details.reason =
 * 'password_reset_required'`, which the SPA detects via
 * `isPasswordResetRequiredDetails` and routes here instead of the login screen).
 *
 * NEW in v2 — the legacy app had no reset flow. Two phases in one view, parity
 * with the LoginView visual language (navy backdrop, white card, gold wordmark):
 *
 *   phase 'request' → email field → POST /auth/request-reset (always 202).
 *     In non-production the response carries `reset_token`, which we pre-fill
 *     into the next phase so the e2e flow (T05) can complete without an inbox.
 *   phase 'set'     → token + new-password fields → POST /auth/reset-password.
 *     Success → onResetComplete() (the host flips back to login with a notice).
 *
 * The view owns only local form state + the two transport calls; it never throws
 * (the transport returns `ApiResult`). An invalid/expired token surfaces as an
 * inline error (parity with the login error line).
 */
import { useState, type FormEvent } from 'react';
import { NEW_PASSWORD_MIN_LENGTH } from '@eer/shared';
import { requestPasswordReset, submitPasswordReset } from './reset-api.js';

export interface ResetPasswordViewProps {
  /** Called after a successful reset (host flips back to the login screen). */
  readonly onResetComplete: () => void;
  /** Called when the user backs out to the login screen without resetting. */
  readonly onBackToLogin?: () => void;
  /** Pre-fill the email (e.g. the address the gated user just tried to log in with). */
  readonly initialEmail?: string;
  /**
   * Pre-fill the one-time code (from an emailed `/auth/reset?token=…` link,
   * E12-F08-T13). When present the view opens directly on the 'set' phase.
   */
  readonly initialToken?: string;
}

type Phase = 'request' | 'set';

export function ResetPasswordView({
  onResetComplete,
  onBackToLogin,
  initialEmail = '',
  initialToken = '',
}: ResetPasswordViewProps): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>(initialToken ? 'set' : 'request');
  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState(initialToken);
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleRequest = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await requestPasswordReset(email.trim());
    setBusy(false);
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    // Anti-enumeration: the server answers the same regardless of existence.
    setNotice(res.data.message);
    // Non-production: the token is echoed so the flow can complete without email.
    if (res.data.reset_token) setToken(res.data.reset_token);
    setPhase('set');
  };

  const handleSet = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await submitPasswordReset(token.trim(), newPassword);
    setBusy(false);
    if (!res.ok) {
      // CONFLICT → token invalid/expired/used; anything else → generic message.
      setError(
        res.error.code === 'CONFLICT'
          ? 'The reset link is invalid or has expired. Please request a new one.'
          : res.error.message,
      );
      return;
    }
    onResetComplete();
  };

  return (
    <div
      id="reset-screen"
      data-testid="reset-screen"
      className="flex min-h-screen items-center justify-center bg-slate-900 p-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-xl">
        <h1 className="brand-font mb-2 text-center text-2xl font-bold tracking-wide text-[#D4AF37]">
          EER GLOBAL
        </h1>
        <p className="mb-6 text-center text-sm text-text-soft">
          {phase === 'request' ? 'Reset password' : 'New password'}
        </p>

        {notice ? (
          <p data-testid="reset-notice" className="mb-4 text-sm text-text-soft" role="status">
            {notice}
          </p>
        ) : null}

        {phase === 'request' ? (
          <form onSubmit={handleRequest} aria-label="Request password reset">
            <label
              className="mb-1 block text-sm font-medium text-text-strong"
              htmlFor="reset-email"
            >
              Email
            </label>
            <input
              id="reset-email"
              data-testid="reset-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-4 w-full rounded border border-border px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
            />

            {error ? (
              <p data-testid="reset-error" className="mb-4 text-sm text-danger" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              data-testid="reset-request-submit"
              disabled={busy}
              className="w-full rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Request reset'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSet} aria-label="Set new password">
            <label
              className="mb-1 block text-sm font-medium text-text-strong"
              htmlFor="reset-token"
            >
              Reset code
            </label>
            <input
              id="reset-token"
              data-testid="reset-token"
              type="text"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="mb-4 w-full rounded border border-border px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
            />

            <label
              className="mb-1 block text-sm font-medium text-text-strong"
              htmlFor="reset-new-password"
            >
              New password
            </label>
            <input
              id="reset-new-password"
              data-testid="reset-new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={NEW_PASSWORD_MIN_LENGTH}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mb-1 w-full rounded border border-border px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
            />
            <p className="mb-4 text-xs text-text-soft">
              At least {NEW_PASSWORD_MIN_LENGTH} characters.
            </p>

            {error ? (
              <p data-testid="reset-error" className="mb-4 text-sm text-danger" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              data-testid="reset-set-submit"
              disabled={busy}
              className="w-full rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Set password'}
            </button>
          </form>
        )}

        {onBackToLogin ? (
          <button
            type="button"
            data-testid="reset-back-to-login"
            onClick={onBackToLogin}
            className="mt-4 w-full text-center text-sm text-text-soft underline hover:text-text-strong"
          >
            Back to login
          </button>
        ) : null}
      </div>
    </div>
  );
}
